import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { apiClient, getErrorMessage } from '../lib/api';

interface Contact {
  name: string;
  email: string;
}

interface ContactsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (emails: string[]) => void;
}

const ContactsModal: React.FC<ContactsModalProps> = ({ isOpen, onClose, onSelect }) => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [filteredContacts, setFilteredContacts] = useState<Contact[]>([]);
  const [selectedEmails, setSelectedEmails] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [needsAuthorization, setNeedsAuthorization] = useState<boolean>(false);

  useEffect(() => {
    if (isOpen) {
      fetchContacts();
    }
  }, [isOpen]);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredContacts(contacts);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = contacts.filter(
        contact =>
          contact.name.toLowerCase().includes(query) ||
          contact.email.toLowerCase().includes(query)
      );
      setFilteredContacts(filtered);
    }
  }, [searchQuery, contacts]);

  const fetchContacts = async () => {
    setIsLoading(true);
    setError('');
    setNeedsAuthorization(false);

    try {
      const response = await apiClient.get('/api/v1/contacts');
      setContacts(response.data.data || []);
      setFilteredContacts(response.data.data || []);
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      setError(errorMessage);

      if (axios.isAxiosError(err) && err.response?.status === 403) {
        setNeedsAuthorization(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleAuthorize = async () => {
    try {
      const redirectUri = `${window.location.origin}/contacts/callback`;
      const response = await apiClient.post('/api/v1/contacts/google/authorize', {
        redirectUri
      });

      const { authUrl } = response.data;
      window.open(authUrl, '_blank');
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const handleToggleSelect = (email: string) => {
    const newSelected = new Set(selectedEmails);
    if (newSelected.has(email)) {
      newSelected.delete(email);
    } else {
      newSelected.add(email);
    }
    setSelectedEmails(newSelected);
  };

  const handleConfirm = () => {
    onSelect(Array.from(selectedEmails));
    handleClose();
  };

  const handleClose = () => {
    setSearchQuery('');
    setSelectedEmails(new Set());
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxHeight: '80vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <h2 className="modal__title">
          連絡先を選択
        </h2>

        {error && (
          <div className="alert alert--error">
            {error}
            {needsAuthorization && (
              <div style={{ marginTop: '0.5rem' }}>
                <p style={{ marginBottom: '0.5rem' }}>
                  Google連絡先にアクセスするには認可が必要です
                </p>
                <button
                  onClick={handleAuthorize}
                  className="btn btn--primary btn--sm"
                >
                  Google連絡先を認可
                </button>
              </div>
            )}
            {!needsAuthorization && (
              <p style={{ marginTop: '0.5rem', fontSize: '0.8rem' }}>
                手動でメールアドレスを入力してください
              </p>
            )}
          </div>
        )}

        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#666', fontSize: '0.875rem' }}>
            読み込み中...
          </div>
        ) : (
          !needsAuthorization &&
          !error && (
            <>
              {/* 検索入力 */}
              <input
                type="text"
                placeholder="検索（名前またはメールアドレス）"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="form-input mb-4"
              />

              {/* 連絡先リスト */}
              <div style={{ flex: 1, overflowY: 'auto', border: '1px solid #ddd', borderRadius: '4px', marginBottom: '1rem' }}>
                {filteredContacts.length === 0 ? (
                  <div style={{ padding: '2rem', textAlign: 'center', color: '#666', fontSize: '0.875rem' }}>
                    該当する連絡先が見つかりません
                  </div>
                ) : (
                  <div>
                    {filteredContacts.map((contact, index) => (
                      <label
                        key={index}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '0.75rem 1rem',
                          borderBottom:
                            index < filteredContacts.length - 1 ? '1px solid #eee' : 'none',
                          cursor: 'pointer',
                          backgroundColor: selectedEmails.has(contact.email)
                            ? '#e0f7fa'
                            : 'transparent'
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selectedEmails.has(contact.email)}
                          onChange={() => handleToggleSelect(contact.email)}
                          style={{ marginRight: '0.75rem', width: '16px', height: '16px', cursor: 'pointer' }}
                        />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 500, color: '#333', fontSize: '0.875rem', marginBottom: '0.25rem' }}>
                            {contact.name}
                          </div>
                          <div style={{ color: '#666', fontSize: '0.75rem' }}>
                            {contact.email}
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </>
          )
        )}

        {/* アクションボタン */}
        <div className="modal__actions">
          <button
            onClick={handleClose}
            className="btn btn--secondary"
          >
            キャンセル
          </button>
          {!needsAuthorization && !isLoading && (
            <button
              onClick={handleConfirm}
              className="btn btn--success"
            >
              選択
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContactsModal;
